import _ from 'lodash';
import React from 'react';
import ErrorBoundary from '../../../shared/error-boundary';
import debounceAction from '../../../shared/debounce-action';
import defaultOnErrorHandler from '../../../shared/default-on-error-handler';
import Loading from '../../../shared/loading';
import mutate from '../../../shared/mutate';
import { defaultProfileCompleteness, profileCompletenessType } from './headers-components/score';
import HeaderWrapper from './headers-components/wrapper';
import { setInvestorType } from '../../../services/profiles/personal-details-service';
import { setInvestmentThemes, getInvestmentThemes } from '../../../services/profiles/investment-information-service';

const desc = 'By telling us a little more about your investment experience and'
  + ' expertise we can find investment insights, which are most relevant to'
  + ' you. Making sure you have the information you need to make better'
  + ' investment decision.';

export default class InvestmentInformationEditor extends React.Component {
  static propTypes = { profileCompleteness: profileCompletenessType, newNavEnabled: false };

  static defaultProps = { profileCompleteness: defaultProfileCompleteness, newNavEnabled: false };

  constructor(props, context) {
    super(props, context);

    this.state = {
      investorTypes: [],
      assetClasses: [],
      investorTypeId: null,
    };

    this.saveInvestorTypeId = debounceAction({
      getCurrentValue: () => this.state.investorTypeId,
      actionPromise: () => setInvestorType(this.state.investorTypeId),
      onBeforeAction: () => this.investorTypeIdLoading.start(),
      onError: e => defaultOnErrorHandler(e, 'Error saving investor type'),
      onAfterAction: () => this.investorTypeIdLoading.stop(),
    });

    this.saveAssetClasses = debounceAction({
      getCurrentValue: () => this.selectedAssetClasses,
      actionPromise: () => setInvestmentThemes(this.selectedAssetClasses),
      onBeforeAction: () => this.assetClassLoading.start(),
      onError: e => defaultOnErrorHandler(e, 'Error saving investment themes'),
      onAfterAction: () => this.assetClassLoading.stop(),
    });
  }

  componentDidMount() {
    this.loading.start();
    getInvestmentThemes()
      .then(result => {
        const {
          asset_classes: assetClasses,
          investor_types: investorTypes,
          profile,
        } = _.get(result, 'data', {});

        this.loading.stop();
        const mappedAssetClasses = (assetClasses || []).map(cls => ({
          ...cls,
          selected: !!profile.investment_theme_asset_class_ids.find(id => id === cls.id),
        }));

        this.setState({
          investorTypes,
          investorTypeId: profile.investor_type_id,
          assetClasses: mappedAssetClasses,
        }, () => {
          this.saveInvestorTypeId.resetInitialValue();
          this.saveAssetClasses.resetInitialValue();
        });
      })
      .catch(e => {
        this.loading.stop();
        defaultOnErrorHandler(e, 'Error loading investment information');
      });
  }

  handleInvestorTypeChange(e) {
    const investorTypeId = e.target.value;

    this.setState({
      investorTypeId,
    }, () => this.saveInvestorTypeId());
  }

  handleAssetClassChange(e) {
    const index = e.target.value;

    this.setState(oldState => mutate(oldState, state => {
      state.assetClasses[index].selected = !state.assetClasses[index].selected;
    }), () => this.saveAssetClasses());
  }

  get selectedAssetClasses() {
    return this.state.assetClasses
      .filter(assetClass => !!assetClass.selected)
      .map(selectedAssetClasses => selectedAssetClasses.id);
  }

  render() {
    return (
      <ErrorBoundary>
        <HeaderWrapper
          title="Investment Information"
          desc={desc}
          profileCompleteness={this.props.profileCompleteness}
          newNavEnabled={this.props.newNavEnabled}
        />
        <Loading ref={el => (this.loading = el)}>
          <section>
            <h3>What type of investor are you?</h3>
            <p>Select the investor type which best describes you.</p>
            <div className="btn-group">
              {this.state.investorTypes.map(type => {
                const matched = this.state.investorTypeId === type.id;
                return (
                  <label
                    htmlFor={`type-${type.id}`}
                    className={`btn ${matched ? 'btn-secondary' : 'btn-default'}`}
                    key={type.id}
                  >
                    <input
                      id={`type-${type.id}`}
                      type="radio"
                      checked={matched}
                      onChange={e => this.handleInvestorTypeChange(e)}
                      value={type.id}
                    />
                    {type.name}
                  </label>
                );
              })}
              <Loading variant="sip" ref={el => (this.investorTypeIdLoading = el)} />
            </div>
            <h3>Which investment themes interest you most?</h3>
            <p>Select the asset classes and investment topics that you are interested in.</p>
            <div className="btn-group">
              {this.state.assetClasses.map((assetClass, index) => (
                <label
                  htmlFor={`asset-class-${assetClass.id}`}
                  className={`btn ${assetClass.selected ? 'btn-secondary' : 'btn-default'}`}
                  key={assetClass.id}
                >
                  <input
                    id={`asset-class-${assetClass.id}`}
                    type="checkbox"
                    value={index}
                    checked={assetClass.selected}
                    onChange={e => this.handleAssetClassChange(e)}
                  />
                  {assetClass.title}
                </label>
              ))}
              <Loading variant="sip" ref={el => (this.assetClassLoading = el)} />
            </div>
          </section>
        </Loading>
      </ErrorBoundary>
    );
  }
}
